/**
 * Workflow Project — the editable results page.
 * Shows live progress while the engine runs, surfaces the one checkpoint
 * (user picks an option then resumes), and renders every completed step
 * as an Asset Block with Copy / Edit / Regenerate.
 */
import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import {
  Copy, RefreshCw, Pencil, Check, ArrowLeft,
  AlertTriangle, Sparkles, ArrowRight, X, Rocket, Wand2,
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import "./workflows.css";

const API = process.env.REACT_APP_BACKEND_URL || '';

// Suggests the next logical workflow once a track completes.
const NEXT_TASK_MAP = {
  youtube: [
    { key: "social", label: "Repurpose to social posts", hint: "Turn this video into Insta, X & LinkedIn captions." },
    { key: "seo", label: "Write the companion blog", hint: "Rank for the same topic on Google." },
  ],
  content: [
    { key: "social", label: "Promote on social", hint: "Generate platform-wise captions for this article." },
    { key: "seo", label: "Build SEO action plan", hint: "Get a 30/60/90-day plan around this keyword." },
  ],
  seo: [
    { key: "content", label: "Write the article", hint: "Convert this brief into a full blog post." },
    { key: "social", label: "Plan promotion posts", hint: "Captions to push this content out." },
  ],
  social: [
    { key: "design", label: "Design the creatives", hint: "Visual concepts for these captions." },
    { key: "youtube", label: "Turn into a YouTube video", hint: "Script the long-form version." },
  ],
  google_ads: [
    { key: "meta_ads", label: "Run Meta Ads too", hint: "Same offer, Insta/FB creative angles." },
    { key: "seo", label: "Build SEO for free traffic", hint: "Stop relying only on paid clicks." },
  ],
  meta_ads: [
    { key: "google_ads", label: "Add Google Search Ads", hint: "Capture high-intent searches too." },
    { key: "design", label: "Design ad creatives", hint: "Lock visuals before launch." },
  ],
  design: [
    { key: "social", label: "Plan posting captions", hint: "Pair these visuals with great copy." },
  ],
  education: [
    { key: "content", label: "Convert lesson to blog", hint: "Long-form version of the lesson." },
    { key: "social", label: "Teaser carousel", hint: "Promote your course on social." },
  ],
};

export default function WorkflowProject() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [checkpointPick, setCheckpointPick] = useState(0);
  const [regenerating, setRegenerating] = useState(null);
  const [showNextTask, setShowNextTask] = useState(false);
  const shownForProjectRef = useRef(null);
  const pollRef = useRef(null);

  const fetchProject = async () => {
    try {
      const res = await axios.get(`${API}/api/workflows/${projectId}`);
      setProject(res.data.project);
      return res.data.project;
    } catch (err) {
      setError(err.response?.data?.detail || "Project not found");
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Poll every 1.5s while status is queued/running, stop when done/awaiting_checkpoint
  useEffect(() => {
    fetchProject();
    pollRef.current = setInterval(async () => {
      const p = await fetchProject();
      if (p && ["done", "awaiting_checkpoint"].includes(p.status)) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    }, 1500);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  // Restart polling whenever status flips back to running (after resume / regenerate)
  useEffect(() => {
    if (!project) return;
    if (["queued", "running"].includes(project.status) && !pollRef.current) {
      pollRef.current = setInterval(async () => {
        const p = await fetchProject();
        if (p && ["done", "awaiting_checkpoint"].includes(p.status)) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      }, 1500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.status]);

  const resumeCheckpoint = async () => {
    try {
      await axios.post(
        `${API}/api/workflows/${projectId}/checkpoint/resume`,
        { selected: checkpointPick },
        { withCredentials: true }
      );
      toast.success("Continuing your workflow…");
      fetchProject();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not resume");
    }
  };

  const regenerate = async (stepId) => {
    setRegenerating(stepId);
    try {
      await axios.post(`${API}/api/workflows/${projectId}/regenerate/${stepId}`);
      toast.info(`Regenerating ${stepId}…`);
      fetchProject();
    } catch (err) {
      toast.error("Could not regenerate");
    } finally {
      setTimeout(() => setRegenerating(null), 2000);
    }
  };

  // Auto-open the "next task" modal once the project flips to done.
  useEffect(() => {
    if (
      project?.status === "done" &&
      shownForProjectRef.current !== project.project_id
    ) {
      shownForProjectRef.current = project.project_id;
      const t = setTimeout(() => setShowNextTask(true), 700);
      return () => clearTimeout(t);
    }
  }, [project?.status, project?.project_id]);

  if (loading) {
    return (
      <>
        <Header />
        <div className="wf-proj"><div className="wf-proj-inner">
          <div className="wf-loading-hero" data-testid="wf-project-loading">
            <div className="wf-loading-spark"><Sparkles size={20}/></div>
            <h2>Bringing up your project…</h2>
            <p>Fetching your workflow state. This takes a second.</p>
            <div className="wf-loading-bar"><span/></div>
          </div>
        </div></div>
      </>
    );
  }
  if (error || !project) {
    return (
      <>
        <Header />
        <div className="wf-proj"><div className="wf-proj-inner">
          <p>{error || "Not found"}</p>
          <button className="wf-btn wf-btn--ghost" onClick={() => navigate("/workflows")}>
            <ArrowLeft size={14}/> Back
          </button>
        </div></div>
      </>
    );
  }

  const steps = project.steps || {};
  const stepIds = Object.keys(steps);
  const doneCount = stepIds.filter((id) => steps[id].status === "done").length;
  const total = stepIds.length;
  const pct = total ? Math.round((doneCount / total) * 100) : 0;
  const currentRunning = stepIds.find((id) => steps[id].status === "running");
  const awaiting = project.awaiting_step;

  return (
    <>
      <Header />
      <div className="wf-proj" data-testid="workflow-project-page">
        <div className="wf-proj-inner">
          <button className="wf-btn wf-btn--ghost" onClick={() => navigate("/workflows")} data-testid="wf-project-back" style={{ marginBottom: 20 }}>
            <ArrowLeft size={14}/> All workflows
          </button>

          <div className="wf-proj-header">
            <div>
              <h1 className="wf-proj-title" data-testid="wf-project-title">{project.track_name}</h1>
              <p className="wf-proj-sub">
                {project.input?.idea ? `"${project.input.idea}"` : "Your project"}
              </p>
            </div>
            <span className="wf-pill live" data-testid="wf-project-status">{project.status.replace("_", " ")}</span>
          </div>

          {/* Progress card */}
          <div className="wf-progress" data-testid="wf-progress">
            <div className="wf-progress-row">
              <strong>
                {project.status === "done"
                  ? "Your package is ready"
                  : awaiting
                  ? "One quick check"
                  : `Generating your package…`}
              </strong>
              <span>{doneCount} of {total} done</span>
            </div>
            <div className="wf-progress-bar"><div className="wf-progress-fill" style={{ width: `${pct}%` }}/></div>
            {project.status !== "done" && !awaiting && (
              <div className="wf-progress-tagline">
                <Wand2 size={14}/>
                <span>
                  {currentRunning
                    ? `Working on: ${steps[currentRunning]?.label || currentRunning}`
                    : "Chaining your tools in the background. Aap yahin ruk sakte ho — output yahi pe aayega."}
                </span>
              </div>
            )}
            <ul className="wf-step-list">
              {stepIds.map((id) => {
                const s = steps[id];
                return (
                  <li key={id} className="wf-step-item" data-status={s.status} data-testid={`wf-step-${id}`}>
                    <span className="wf-step-dot"/>
                    <span style={{ flex: 1 }}>{s.label || id}</span>
                    <span style={{ fontSize: "0.8rem", textTransform: "uppercase", letterSpacing: "0.08em" }}>
                      {s.status === "running" ? "Running" : s.status === "done" ? "✓" : s.status === "needs_retry" ? "Retry" : ""}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>

          {/* Checkpoint UI */}
          {awaiting && steps[awaiting]?.output?.options && (
            <CheckpointBlock
              step={steps[awaiting]}
              picked={checkpointPick}
              onPick={setCheckpointPick}
              onConfirm={resumeCheckpoint}
            />
          )}

          {/* Asset blocks for every completed step */}
          {stepIds.map((id) => {
            const s = steps[id];
            if (s.status !== "done" || !s.output) return null;
            return (
              <AssetBlock
                key={id}
                stepId={id}
                step={s}
                projectId={projectId}
                regenerating={regenerating === id}
                onRegenerate={() => regenerate(id)}
                onEdited={fetchProject}
              />
            );
          })}

          {project.status === "done" && (
            <div className="wf-celebrate" data-testid="wf-celebrate">
              <h3>🎉 Aapka package ready hai!</h3>
              <p>Sab kuch generate ho gaya. Kisi bhi block ko Copy / Edit / Regenerate karein — ya next workflow chalu karein.</p>
              <div className="wf-celebrate-actions">
                <button onClick={() => setShowNextTask(true)} data-testid="wf-celebrate-next" className="wf-celebrate-primary">
                  <Rocket size={14}/> Perform next task
                </button>
                <button onClick={() => navigate("/workflows")} data-testid="wf-celebrate-new">
                  <Sparkles size={14}/> Naya workflow
                </button>
                <button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} data-testid="wf-celebrate-top">
                  ⬆ Top par jao
                </button>
              </div>
            </div>
          )}

          {showNextTask && project.status === "done" && (
            <NextTaskModal
              trackKey={project.track_key}
              projectIdea={project.input?.idea || project.input?.topic || project.input?.keyword || ""}
              onClose={() => setShowNextTask(false)}
              onPick={(nextKey) => {
                setShowNextTask(false);
                navigate(`/workflows/${nextKey}`);
              }}
            />
          )}
        </div>
      </div>
      <Footer />
    </>
  );
}

function CheckpointBlock({ step, picked, onPick, onConfirm }) {
  const opts = step.output?.options || [];
  return (
    <div className="wf-checkpoint" data-testid="wf-checkpoint">
      <h3 className="wf-checkpoint-title">{step.output?.title || "Quick check before we continue"}</h3>
      <p style={{ color: "#7c2d12", fontSize: "0.9rem", margin: 0 }}>
        This decision shapes the rest of your package — pick one and we&rsquo;ll run the rest.
      </p>
      <div className="wf-checkpoint-options">
        {opts.map((opt, i) => (
          <button
            key={i}
            type="button"
            className="wf-option"
            data-selected={picked === i}
            onClick={() => onPick(i)}
            data-testid={`wf-checkpoint-opt-${i}`}
          >{opt}</button>
        ))}
      </div>
      <div className="wf-checkpoint-actions">
        <button className="wf-btn" onClick={onConfirm} data-testid="wf-checkpoint-confirm">
          <Check size={14}/> Continue with this
        </button>
      </div>
    </div>
  );
}

function AssetBlock({ stepId, step, projectId, regenerating, onRegenerate, onEdited }) {
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  const out = step.output || {};
  const renderable = renderOutput(out);
  const toolCta = toolLinkForStep(stepId, out);

  const copyAll = () => {
    const text = textForCopy(out);
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  };

  const startEdit = () => {
    setDraft(JSON.stringify(out, null, 2));
    setEditing(true);
  };

  const saveEdit = async () => {
    try {
      const parsed = JSON.parse(draft);
      await axios.patch(
        `${API}/api/workflows/${projectId}/step/${stepId}`,
        { output: parsed },
      );
      toast.success("Saved");
      setEditing(false);
      onEdited && onEdited();
    } catch (err) {
      toast.error("Invalid JSON — fix and try again");
    }
  };

  return (
    <div className="wf-asset" data-testid={`wf-asset-${stepId}`}>
      <div className="wf-asset-head">
        <h3 className="wf-asset-title">{step.label}</h3>
        <div className="wf-asset-actions">
          <button onClick={copyAll} data-testid={`wf-asset-copy-${stepId}`}><Copy size={13}/> Copy</button>
          <button onClick={editing ? saveEdit : startEdit} data-testid={`wf-asset-edit-${stepId}`}>
            {editing ? <><Check size={13}/> Save</> : <><Pencil size={13}/> Edit</>}
          </button>
          <button onClick={onRegenerate} disabled={regenerating} data-testid={`wf-asset-regen-${stepId}`}>
            <RefreshCw size={13} className={regenerating ? "animate-spin" : ""}/> Regenerate
          </button>
        </div>
      </div>
      {editing ? (
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={10}
          style={{ width: "100%", fontFamily: "ui-monospace, monospace", fontSize: "0.85rem", padding: 12, border: "1px solid rgba(109,40,217,0.18)", borderRadius: 8 }}
        />
      ) : renderable}
      {!editing && toolCta && (
        <button
          className="wf-tool-cta"
          onClick={() => navigate(toolCta.to)}
          data-testid={`wf-tool-cta-${stepId}`}
          title={toolCta.hint || ""}
        >
          ✨ {toolCta.label} →
        </button>
      )}
    </div>
  );
}

function toolLinkForStep(stepId, out) {
  // Map workflow asset blocks → standalone tool deep-links
  const map = {
    thumbnail: { label: "Open Thumbnail Designer with these concepts", to: "/tools" },
    article: { label: "Open AI Content Writer for follow-up posts", to: "/tools" },
    slides: { label: "Open AI Slide Generator", to: "/tools/ai-slides" },
    captions: { label: "Open SRT Caption Generator", to: "/tools/srt-captions" },
    pdf_export: { label: "Open PDF tools", to: "/tools" },
    hashtags: { label: "Browse Hashtag Generator", to: "/tools" },
    design: { label: "Open Design tools (background remover, banner)", to: "/tools" },
    asset: { label: "Open Design tools to render this asset", to: "/tools" },
    site_audit: { label: "Re-run Site Audit on demand", to: "/tools" },
    keyword_research: { label: "Open Keyword Research tool", to: "/tools" },
    backlinks: { label: "Open Backlink Analyzer", to: "/tools" },
  };
  return map[stepId] || null;
}

function renderOutput(out) {
  if (!out) return null;
  if (out.type === "options") {
    return (
      <ul style={{ paddingLeft: 18, color: "#1a1422", lineHeight: 1.7 }}>
        {(out.options || []).map((o, i) => (
          <li key={i} style={{ fontWeight: i === (out.selected ?? 0) ? 600 : 400 }}>
            {o}{i === (out.selected ?? 0) ? <span style={{ color: "#6d28d9", marginLeft: 8, fontSize: "0.8rem" }}>← picked</span> : null}
          </li>
        ))}
      </ul>
    );
  }
  if (out.type === "outline") {
    return (
      <div>
        {out.angle && <p style={{ margin: "0 0 14px", color: "#5a4d6f" }}><strong>Angle:</strong> {out.angle}</p>}
        {(out.sections || []).map((sec, i) => (
          <div key={i} style={{ marginBottom: 14 }}>
            <h4 style={{ margin: "0 0 6px", fontSize: "0.95rem", color: "#4c1d95" }}>{sec.heading}</h4>
            <ul style={{ paddingLeft: 18, lineHeight: 1.65, margin: 0 }}>
              {(sec.bullets || []).map((b, j) => <li key={j}>{b}</li>)}
            </ul>
          </div>
        ))}
      </div>
    );
  }
  if (out.type === "captions") {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {(out.variants || []).map((v, i) => {
          const full = `${v.text}${v.hashtags?.length ? "\n" + v.hashtags.join(" ") : ""}`;
          return (
            <div key={i} style={{ background: "#fbf8f3", borderRadius: 8, padding: "12px 14px" }}>
              <div style={{ display: "flex", alignItems: "center", marginBottom: 6 }}>
                <div style={{ fontSize: "0.75rem", color: "#6d28d9", fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase" }}>{v.platform}</div>
                <CopyChip value={full}/>
              </div>
              <div style={{ whiteSpace: "pre-wrap", lineHeight: 1.6 }}>{v.text}</div>
              {v.hashtags?.length > 0 && (
                <div style={{ marginTop: 8, color: "#5a4d6f", fontSize: "0.85rem" }}>{v.hashtags.join(" ")}</div>
              )}
            </div>
          );
        })}
      </div>
    );
  }
  if (out.type === "trending") {
    return (
      <div>
        {out.topics?.length > 0 && (
          <>
            <SectionLabel copyValue={(out.topics || []).map((t) => typeof t === "string" ? t : (t.TOPIC || t.topic || t.VIDEO_IDEA || "")).filter(Boolean).join("\n")}>
              Trending in {out.niche}
            </SectionLabel>
            <ul style={{ paddingLeft: 18, lineHeight: 1.7, marginBottom: 14 }}>
              {out.topics.map((t, i) => (
                <li key={i}>{typeof t === "string" ? t : (t.TOPIC || t.topic || t.VIDEO_IDEA || JSON.stringify(t))}</li>
              ))}
            </ul>
          </>
        )}
        {out.keywords?.length > 0 && (
          <>
            <SectionLabel copyValue={(out.keywords || []).map((k) => typeof k === "string" ? k : (k.keyword || k.text || "")).filter(Boolean).join(", ")}>
              Keyword opportunities
            </SectionLabel>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {out.keywords.map((k, i) => (
                <span key={i} style={{ background: "#ede9fe", color: "#4c1d95", padding: "4px 10px", borderRadius: 999, fontSize: "0.85rem" }}>
                  {typeof k === "string" ? k : (k.keyword || k.text || JSON.stringify(k))}
                </span>
              ))}
            </div>
          </>
        )}
      </div>
    );
  }
  if (out.type === "hook_script") {
    return (
      <div>
        {out.hooks?.length > 0 && (
          <>
            <SectionLabel copyValue={out.hooks.join("\n")}>Hook variants</SectionLabel>
            <ul style={{ paddingLeft: 18, lineHeight: 1.65, marginBottom: 18 }}>
              {out.hooks.map((h, i) => <li key={i}>{h}</li>)}
            </ul>
          </>
        )}
        {out.script && (
          <>
            <SectionLabel copyValue={out.script}>Full script</SectionLabel>
            <div className="wf-script-card">
              <Markdown>{out.script}</Markdown>
            </div>
          </>
        )}
      </div>
    );
  }
  if (out.type === "thumbnail") {
    return (
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
        {(out.variants || []).map((v, i) => (
          <div key={i} style={{ background: "#fbf8f3", borderRadius: 10, padding: 16 }}>
            <div style={{ fontSize: "1.05rem", fontWeight: 700, color: "#1a1422", marginBottom: 8 }}>{v.text}</div>
            <div style={{ fontSize: "0.85rem", color: "#5a4d6f", lineHeight: 1.5 }}>{v.concept}</div>
          </div>
        ))}
      </div>
    );
  }
  if (out.type === "metadata") {
    return (
      <div>
        {out.description && (
          <>
            <SectionLabel copyValue={out.description}>SEO description</SectionLabel>
            <div style={{ background: "#fbf8f3", borderRadius: 8, padding: "12px 14px", fontSize: "0.92rem", lineHeight: 1.6, marginBottom: 14 }}>
              <Markdown>{out.description}</Markdown>
            </div>
          </>
        )}
        {out.tags?.length > 0 && (
          <>
            <SectionLabel copyValue={out.tags.join(", ")}>Tags</SectionLabel>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 14 }}>
              {out.tags.map((t, i) => (
                <span key={i} style={{ background: "#ede9fe", color: "#4c1d95", padding: "3px 9px", borderRadius: 6, fontSize: "0.82rem" }}>{t}</span>
              ))}
            </div>
          </>
        )}
        {out.hashtags?.length > 0 && (
          <>
            <SectionLabel copyValue={out.hashtags.join(" ")}>Hashtags</SectionLabel>
            <div style={{ color: "#5a4d6f", fontSize: "0.9rem", lineHeight: 1.7 }}>{out.hashtags.join(" ")}</div>
          </>
        )}
      </div>
    );
  }
  if (out.type === "upload_time") {
    const list = (out.recommendations || []).map((r) => {
      if (typeof r === "string") return r;
      if (!r) return "";
      const day = r.day || r.weekday || "";
      const time = r.time || r.slot || "";
      return [day, time].filter(Boolean).join(" • ");
    }).filter(Boolean);
    return (
      <div>
        {list.length > 0 ? (
          <div className="wf-time-grid" data-testid="wf-upload-time-grid">
            {list.map((t, i) => (
              <div key={i} className="wf-time-chip">{t}</div>
            ))}
          </div>
        ) : (
          <p style={{ color: "#5a4d6f" }}>No upload time recommendations.</p>
        )}
      </div>
    );
  }
  if (out.type === "repurpose") {
    return (
      <div>
        {out.shorts_scripts?.length > 0 && (
          <div style={{ marginBottom: 18 }}>
            <h5 style={{ margin: "0 0 8px", color: "#4c1d95", fontSize: "0.85rem", letterSpacing: "0.08em", textTransform: "uppercase" }}>Shorts scripts</h5>
            {out.shorts_scripts.map((s, i) => (
              <div key={i} style={{ whiteSpace: "pre-wrap", background: "#fbf8f3", borderRadius: 8, padding: "10px 14px", fontSize: "0.9rem", lineHeight: 1.55, marginBottom: 8 }}>{s}</div>
            ))}
          </div>
        )}
        {out.shorts_ideas?.length > 0 && (
          <div style={{ marginBottom: 18 }}>
            <h5 style={{ margin: "0 0 8px", color: "#4c1d95", fontSize: "0.85rem", letterSpacing: "0.08em", textTransform: "uppercase" }}>More shorts ideas</h5>
            <ul style={{ paddingLeft: 18, lineHeight: 1.65 }}>{out.shorts_ideas.map((i, k) => <li key={k}>{i}</li>)}</ul>
          </div>
        )}
        {out.captions?.length > 0 && (
          <div style={{ marginBottom: 18 }}>
            <h5 style={{ margin: "0 0 8px", color: "#4c1d95", fontSize: "0.85rem", letterSpacing: "0.08em", textTransform: "uppercase" }}>Social captions</h5>
            {out.captions.map((c, i) => (
              <div key={i} style={{ background: "#fbf8f3", borderRadius: 8, padding: "10px 14px", marginBottom: 8 }}>
                <div style={{ fontSize: "0.72rem", color: "#6d28d9", fontWeight: 600, marginBottom: 4, letterSpacing: "0.08em", textTransform: "uppercase" }}>{c.platform}</div>
                <div style={{ whiteSpace: "pre-wrap", fontSize: "0.9rem", lineHeight: 1.55 }}>{c.text}</div>
              </div>
            ))}
          </div>
        )}
        {out.blog_outline && (
          <div>
            <h5 style={{ margin: "0 0 8px", color: "#4c1d95", fontSize: "0.85rem", letterSpacing: "0.08em", textTransform: "uppercase" }}>Blog outline</h5>
            <p style={{ margin: "0 0 8px", fontWeight: 600 }}>{out.blog_outline.title}</p>
            {(out.blog_outline.sections || []).map((sec, i) => (
              <div key={i} style={{ marginBottom: 10 }}>
                <div style={{ color: "#4c1d95", fontWeight: 600, fontSize: "0.92rem" }}>{sec.h}</div>
                <ul style={{ paddingLeft: 18, margin: "4px 0 0", lineHeight: 1.5, fontSize: "0.9rem" }}>{(sec.bullets || []).map((b, j) => <li key={j}>{b}</li>)}</ul>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }
  if (out.type === "topic_research") {
    return (
      <ul style={{ paddingLeft: 0, listStyle: "none", margin: 0 }}>
        {(out.topics || []).map((t, i) => (
          <li key={i} style={{ background: "#fbf8f3", padding: "12px 14px", borderRadius: 8, marginBottom: 8 }}>
            <div style={{ fontWeight: 600 }}>{t.title}</div>
            <div style={{ fontSize: "0.82rem", color: "#5a4d6f", marginTop: 3 }}>
              <span style={{ color: "#6d28d9" }}>kw:</span> {t.keyword} • <span style={{ color: "#6d28d9" }}>type:</span> {t.type} • <span style={{ color: "#6d28d9" }}>intent:</span> {t.intent}{t.difficulty ? ` • difficulty: ${t.difficulty}` : ""}
            </div>
          </li>
        ))}
      </ul>
    );
  }
  if (out.type === "article") {
    return (
      <div>
        <div style={{ fontSize: "0.85rem", color: "#5a4d6f", marginBottom: 10 }}>
          <strong>Primary kw:</strong> {out.primary_keyword} • <strong>Words:</strong> {out.word_count} • <strong>Keywords:</strong> {(out.keywords_used || []).slice(0,5).join(", ")}
        </div>
        <div className="wf-article-card">
          <Markdown>{out.markdown}</Markdown>
        </div>
      </div>
    );
  }
  if (out.type === "quality") {
    const m = out.metrics || {};
    return (
      <div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10, marginBottom: 14 }}>
          {[
            ["Word count", m.word_count],
            ["Sentences", m.sentences],
            ["Flesch score", m.flesch_score],
            ["Reading grade", m.grade],
            ["Keyword density", m.keyword_density_pct ? `${m.keyword_density_pct}%` : "—"],
            ["Primary keyword", m.primary_keyword],
          ].map(([k, v]) => (
            <div key={k} style={{ background: "#fbf8f3", borderRadius: 8, padding: "10px 12px" }}>
              <div style={{ fontSize: "0.72rem", color: "#6d28d9", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 4 }}>{k}</div>
              <div style={{ fontSize: "1rem", fontWeight: 600, color: "#1a1422" }}>{v || "—"}</div>
            </div>
          ))}
        </div>
        <h5 style={{ margin: "0 0 8px", color: "#4c1d95", fontSize: "0.85rem", letterSpacing: "0.08em", textTransform: "uppercase" }}>Notes</h5>
        <ul style={{ paddingLeft: 18, lineHeight: 1.6 }}>{(out.notes || []).map((n, i) => <li key={i}>{n}</li>)}</ul>
      </div>
    );
  }
  if (out.type === "headlines") {
    return (
      <ul style={{ paddingLeft: 18, lineHeight: 1.7 }}>
        {(out.variants || []).map((v, i) => <li key={i}>{v}</li>)}
      </ul>
    );
  }
  if (out.type === "audit") {
    if (out.error) return <p style={{ color: "#dc2626" }}>{out.error}</p>;
    return (
      <div>
        <div style={{ display: "flex", gap: 18, flexWrap: "wrap", marginBottom: 14 }}>
          {out.score != null && <Metric label="SEO score" value={out.score}/>}
          {out.pages_crawled != null && <Metric label="Pages crawled" value={out.pages_crawled}/>}
        </div>
        {out.issues?.length > 0 && (
          <>
            <h5 style={{ margin: "0 0 6px", color: "#4c1d95", fontSize: "0.85rem", letterSpacing: "0.08em", textTransform: "uppercase" }}>Top issues</h5>
            <ul style={{ paddingLeft: 18, lineHeight: 1.55, marginBottom: 14 }}>
              {out.issues.map((iss, i) => (
                <li key={i}>{typeof iss === "string" ? iss : (iss.message || iss.title || JSON.stringify(iss))}</li>
              ))}
            </ul>
          </>
        )}
        {out.recommendations?.length > 0 && (
          <>
            <h5 style={{ margin: "0 0 6px", color: "#4c1d95", fontSize: "0.85rem", letterSpacing: "0.08em", textTransform: "uppercase" }}>Recommendations</h5>
            <ul style={{ paddingLeft: 18, lineHeight: 1.55 }}>
              {out.recommendations.map((r, i) => {
                if (typeof r === "string") return <li key={i}>{r}</li>;
                if (r && typeof r === "object") {
                  const action = r.action || r.message || r.title;
                  const priority = r.priority || r.severity;
                  return (
                    <li key={i}>
                      {priority && <span style={{ background: "#ede9fe", color: "#4c1d95", padding: "1px 7px", borderRadius: 4, fontSize: "0.72rem", marginRight: 6, textTransform: "uppercase", letterSpacing: "0.04em" }}>{priority}</span>}
                      {action || JSON.stringify(r)}
                    </li>
                  );
                }
                return <li key={i}>{JSON.stringify(r)}</li>;
              })}
            </ul>
          </>
        )}
      </div>
    );
  }
  if (out.type === "competitors") {
    return (
      <div>
        {out.primary_keyword && <p style={{ marginTop: 0, color: "#5a4d6f" }}><strong>Primary:</strong> {out.primary_keyword}</p>}
        {out.competitors?.length > 0 ? (
          <ul style={{ paddingLeft: 0, listStyle: "none", margin: 0 }}>
            {out.competitors.map((c, i) => (
              <li key={i} style={{ background: "#fbf8f3", padding: "10px 12px", borderRadius: 8, marginBottom: 8 }}>
                <div style={{ fontWeight: 600 }}>{c.domain || c.url || c.name || "Competitor"}</div>
                {(c.title || c.summary) && <div style={{ fontSize: "0.85rem", color: "#5a4d6f", marginTop: 3 }}>{c.title || c.summary}</div>}
              </li>
            ))}
          </ul>
        ) : <p style={{ color: "#5a4d6f" }}>No competitor data available.</p>}
        {out.summary && typeof out.summary === "string" && (
          <p style={{ marginTop: 12, lineHeight: 1.55 }}>{out.summary}</p>
        )}
      </div>
    );
  }
  if (out.type === "backlinks") {
    if (out.error) return <p style={{ color: "#dc2626" }}>{out.error}</p>;
    return (
      <div>
        <div style={{ display: "flex", gap: 18, flexWrap: "wrap", marginBottom: 14 }}>
          {out.total_backlinks != null && <Metric label="Total backlinks" value={out.total_backlinks}/>}
          {out.referring_domains != null && <Metric label="Referring domains" value={out.referring_domains}/>}
          {out.domain_authority != null && <Metric label="Domain authority" value={out.domain_authority}/>}
        </div>
        {out.highlights?.length > 0 && (
          <>
            <h5 style={{ margin: "0 0 6px", color: "#4c1d95", fontSize: "0.85rem", letterSpacing: "0.08em", textTransform: "uppercase" }}>Top backlinks</h5>
            <ul style={{ paddingLeft: 18, lineHeight: 1.55 }}>
              {out.highlights.map((b, i) => {
                if (typeof b === "string") return <li key={i}>{b}</li>;
                if (b && typeof b === "object") {
                  return (
                    <li key={i}>
                      <a href={b.source_url || b.url} target="_blank" rel="noreferrer" style={{ color: "#6d28d9" }}>
                        {b.source_url || b.url || b.domain}
                      </a>
                      {b.anchor_text && <span style={{ color: "#5a4d6f" }}> — "{b.anchor_text}"</span>}
                      {b.domain_authority != null && <span style={{ background: "#ede9fe", color: "#4c1d95", padding: "1px 6px", borderRadius: 4, fontSize: "0.72rem", marginLeft: 6 }}>DA {b.domain_authority}</span>}
                    </li>
                  );
                }
                return <li key={i}>{JSON.stringify(b)}</li>;
              })}
            </ul>
          </>
        )}
        {out.summary && <p style={{ marginTop: 12, lineHeight: 1.55 }}>{out.summary}</p>}
      </div>
    );
  }
  if (out.type === "rank_tracker") {
    return (
      <div>
        <p style={{ marginTop: 0, color: "#5a4d6f" }}><strong>Domain:</strong> {out.domain}</p>
        <h5 style={{ margin: "0 0 6px", color: "#4c1d95", fontSize: "0.85rem", letterSpacing: "0.08em", textTransform: "uppercase" }}>Keywords to track</h5>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
          {(out.keywords || []).map((k, i) => (
            <span key={i} style={{ background: "#ede9fe", color: "#4c1d95", padding: "3px 9px", borderRadius: 6, fontSize: "0.82rem" }}>{k}</span>
          ))}
        </div>
        <p style={{ fontSize: "0.85rem", color: "#5a4d6f" }}>{out.note}</p>
      </div>
    );
  }
  if (out.type === "action_plan") {
    const planText = typeof out.plan === "string"
      ? out.plan
      : (Array.isArray(out.plan) ? out.plan.join("\n") : JSON.stringify(out.plan));
    return (
      <div>
        {out.primary_keyword && <p style={{ marginTop: 0, color: "#5a4d6f" }}><strong>Primary:</strong> {out.primary_keyword}</p>}
        <SectionLabel copyValue={planText}>30/60/90-day plan</SectionLabel>
        <div className="wf-article-card" style={{ marginBottom: 16 }}>
          <Markdown>{planText}</Markdown>
        </div>
        {out.content_briefs?.length > 0 && (
          <>
            <h5 style={{ margin: "0 0 6px", color: "#4c1d95", fontSize: "0.85rem", letterSpacing: "0.08em", textTransform: "uppercase" }}>Content briefs</h5>
            {out.content_briefs.map((b, i) => (
              <div key={i} style={{ background: "#fbf8f3", borderRadius: 8, padding: "10px 12px", marginBottom: 8 }}>
                <div style={{ fontWeight: 600 }}>{b.title}</div>
                <div style={{ fontSize: "0.85rem", color: "#5a4d6f", marginTop: 3 }}>
                  Primary: <strong>{b.primary}</strong> • {b.words || ""} words
                  {Array.isArray(b.secondary) && b.secondary.length > 0 && <> • Secondary: {b.secondary.slice(0,4).join(", ")}</>}
                </div>
                {b.links && <div style={{ fontSize: "0.82rem", color: "#5a4d6f", marginTop: 3 }}>Links: {typeof b.links === "string" ? b.links : JSON.stringify(b.links)}</div>}
              </div>
            ))}
          </>
        )}
      </div>
    );
  }
  if (out.type === "hashtag_pack") {
    return (
      <div>
        <p style={{ marginTop: 0, color: "#5a4d6f" }}><strong>Platform:</strong> {out.platform}</p>
        {(out.all || []).length > 0 && (
          <>
            <SectionLabel copyValue={(out.all || []).join(" ")}>Hashtag pack</SectionLabel>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
              {out.all.map((t, i) => (
                <span key={i} style={{ background: "#ede9fe", color: "#4c1d95", padding: "3px 9px", borderRadius: 6, fontSize: "0.82rem" }}>{t}</span>
              ))}
            </div>
          </>
        )}
      </div>
    );
  }
  if (out.type === "design_brief") {
    return (
      <div>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 10, fontSize: "0.85rem", color: "#5a4d6f" }}>
          <span><strong>Platform:</strong> {out.platform}</span>
          <span><strong>Size:</strong> {out.size}</span>
        </div>
        <div className="wf-article-card">
          <Markdown>{out.brief}</Markdown>
        </div>
        {out.caption_used && <p style={{ marginTop: 10, color: "#5a4d6f", fontSize: "0.85rem" }}><strong>Caption:</strong> {out.caption_used}</p>}
      </div>
    );
  }
  if (out.type === "schedule") {
    return (
      <div>
        <div className="wf-time-grid">
          {(out.plan || []).map((p, i) => (
            <div key={i} className="wf-time-platform">
              <div className="wf-time-platform-name">{p.platform}</div>
              <div className="wf-time-chip-list">
                {(p.best_times || []).map((t, j) => (
                  <span key={j} className="wf-time-chip">{t}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }
  if (out.type === "ad_groups") {
    return (
      <div>
        {(out.groups || []).map((g, i) => (
          <div key={i} style={{ background: "#fbf8f3", borderRadius: 8, padding: "12px 14px", marginBottom: 10 }}>
            <div style={{ fontWeight: 600, fontSize: "1rem" }}>{g.name}</div>
            {g.theme && <div style={{ fontSize: "0.85rem", color: "#5a4d6f", margin: "2px 0 8px" }}>{g.theme}</div>}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {(g.keywords || []).map((k, j) => (
                <span key={j} style={{ background: "#ede9fe", color: "#4c1d95", padding: "3px 9px", borderRadius: 6, fontSize: "0.82rem" }}>{k}</span>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }
  if (out.type === "ad_copy") {
    return (
      <div>
        {(out.groups || []).map((g, i) => (
          <div key={i} style={{ background: "#fbf8f3", borderRadius: 8, padding: "12px 14px", marginBottom: 10 }}>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>{g.ad_group}</div>
            <h6 style={{ margin: "0 0 4px", color: "#6d28d9", fontSize: "0.72rem", letterSpacing: "0.08em", textTransform: "uppercase" }}>Headlines</h6>
            <ul style={{ paddingLeft: 18, margin: "0 0 8px", lineHeight: 1.5 }}>{(g.headlines || []).map((h, j) => <li key={j}>{h}</li>)}</ul>
            <h6 style={{ margin: "0 0 4px", color: "#6d28d9", fontSize: "0.72rem", letterSpacing: "0.08em", textTransform: "uppercase" }}>Descriptions</h6>
            <ul style={{ paddingLeft: 18, margin: 0, lineHeight: 1.5 }}>{(g.descriptions || []).map((d, j) => <li key={j}>{d}</li>)}</ul>
          </div>
        ))}
      </div>
    );
  }
  if (out.type === "landing_check") {
    return (
      <div>
        {out.primary_keyword && <p style={{ marginTop: 0, color: "#5a4d6f" }}><strong>For keyword:</strong> {out.primary_keyword}</p>}
        <ul style={{ paddingLeft: 18, lineHeight: 1.65 }}>
          {(out.checklist || []).map((c, i) => <li key={i}>{c}</li>)}
        </ul>
      </div>
    );
  }
  if (out.type === "budget") {
    return (
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 10 }}>
        {[
          ["Daily budget", `₹${out.daily_budget_inr || "—"}`],
          ["Goal", out.goal],
          ["CPC range", out.cpc_range],
          ["Clicks/day", out.clicks_per_day],
          ["Conversions/day", out.conversions_per_day],
          ["Bidding strategy", out.bidding_strategy],
        ].map(([k, v]) => (
          <div key={k} style={{ background: "#fbf8f3", borderRadius: 8, padding: "10px 12px" }}>
            <div style={{ fontSize: "0.7rem", color: "#6d28d9", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 3 }}>{k}</div>
            <div style={{ fontSize: "0.95rem", fontWeight: 600, color: "#1a1422" }}>{v || "—"}</div>
          </div>
        ))}
        {out.optimization_tip && (
          <div style={{ gridColumn: "1 / -1", background: "#fef3c7", color: "#7c2d12", borderRadius: 8, padding: "10px 14px", fontSize: "0.9rem" }}>
            💡 {out.optimization_tip}
          </div>
        )}
      </div>
    );
  }
  if (out.type === "campaign_export") {
    return (
      <div>
        {out.summary && (
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 12, fontSize: "0.85rem", color: "#5a4d6f" }}>
            {Object.entries(out.summary).filter(([, v]) => v != null).map(([k, v]) => (
              <span key={k}><strong>{k.replace(/_/g, " ")}:</strong> {String(v)}</span>
            ))}
          </div>
        )}
        <div style={{ maxHeight: 360, overflowY: "auto", background: "#fbf8f3", borderRadius: 8, padding: "10px 12px" }}>
          <table style={{ width: "100%", fontSize: "0.82rem", borderCollapse: "collapse" }}>
            <thead><tr>
              {["Ad group", "Keyword", "Match", "Headline 1", "Headline 2", "Description 1"].map((h) => (
                <th key={h} style={{ textAlign: "left", color: "#6d28d9", padding: "6px 8px", borderBottom: "1px solid rgba(109,40,217,0.18)" }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {(out.rows || []).map((r, i) => (
                <tr key={i}>
                  <td style={{ padding: "5px 8px", borderBottom: "1px dashed rgba(109,40,217,0.08)" }}>{r.ad_group}</td>
                  <td style={{ padding: "5px 8px", borderBottom: "1px dashed rgba(109,40,217,0.08)" }}>{r.keyword}</td>
                  <td style={{ padding: "5px 8px", borderBottom: "1px dashed rgba(109,40,217,0.08)" }}>{r.match_type}</td>
                  <td style={{ padding: "5px 8px", borderBottom: "1px dashed rgba(109,40,217,0.08)" }}>{r.headline_1}</td>
                  <td style={{ padding: "5px 8px", borderBottom: "1px dashed rgba(109,40,217,0.08)" }}>{r.headline_2}</td>
                  <td style={{ padding: "5px 8px", borderBottom: "1px dashed rgba(109,40,217,0.08)" }}>{r.description_1}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }
  if (out.type === "meta_ad_copy") {
    return (
      <div>
        {out.angle && <p style={{ marginTop: 0, color: "#5a4d6f" }}><strong>Angle:</strong> {out.angle}</p>}
        {(out.units || []).map((u, i) => (
          <div key={i} style={{ background: "#fbf8f3", borderRadius: 8, padding: "12px 14px", marginBottom: 10 }}>
            <div style={{ fontSize: "0.72rem", color: "#6d28d9", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 6 }}>Variant {i + 1}</div>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>{u.hook}</div>
            <div style={{ marginBottom: 6, lineHeight: 1.55 }}>{u.body}</div>
            <div style={{ display: "inline-block", background: "#6d28d9", color: "white", padding: "4px 12px", borderRadius: 999, fontSize: "0.82rem", fontWeight: 600 }}>{u.cta}</div>
          </div>
        ))}
      </div>
    );
  }
  if (out.type === "meta_creatives") {
    return (
      <div>
        {(out.concepts || []).map((c, i) => (
          <div key={i} style={{ background: "#fbf8f3", borderRadius: 8, padding: "12px 14px", marginBottom: 10 }}>
            <div style={{ fontSize: "0.72rem", color: "#6d28d9", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 6 }}>{c.format}</div>
            <div style={{ fontSize: "0.9rem", lineHeight: 1.55, marginBottom: 6 }}><strong>Composition:</strong> {c.composition}</div>
            <div style={{ fontSize: "0.9rem", lineHeight: 1.55, marginBottom: 6 }}><strong>Visual:</strong> {c.visual}</div>
            <div style={{ display: "inline-block", background: "#1a1422", color: "white", padding: "3px 10px", borderRadius: 4, fontSize: "0.85rem", fontWeight: 700, letterSpacing: "0.02em" }}>{c.text_overlay}</div>
          </div>
        ))}
      </div>
    );
  }
  if (out.type === "ab_matrix") {
    return (
      <div>
        <div style={{ overflowX: "auto", background: "#fbf8f3", borderRadius: 8, padding: "10px 12px", marginBottom: 12 }}>
          <table style={{ width: "100%", fontSize: "0.85rem", borderCollapse: "collapse" }}>
            <thead><tr>
              {["Variant", "Hook", "Creative", "Overlay", "CTA"].map((h) => (
                <th key={h} style={{ textAlign: "left", color: "#6d28d9", padding: "6px 8px", borderBottom: "1px solid rgba(109,40,217,0.18)" }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {(out.variants || []).map((v, i) => (
                <tr key={i}>
                  <td style={{ padding: "5px 8px", borderBottom: "1px dashed rgba(109,40,217,0.08)", fontWeight: 600, color: "#6d28d9" }}>{v.variant}</td>
                  <td style={{ padding: "5px 8px", borderBottom: "1px dashed rgba(109,40,217,0.08)" }}>{v.hook}</td>
                  <td style={{ padding: "5px 8px", borderBottom: "1px dashed rgba(109,40,217,0.08)" }}>{v.creative_format}</td>
                  <td style={{ padding: "5px 8px", borderBottom: "1px dashed rgba(109,40,217,0.08)" }}>{v.text_overlay}</td>
                  <td style={{ padding: "5px 8px", borderBottom: "1px dashed rgba(109,40,217,0.08)" }}>{v.cta}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {out.recommendation && <p style={{ fontSize: "0.85rem", color: "#5a4d6f", margin: 0 }}>💡 {out.recommendation}</p>}
      </div>
    );
  }
  if (out.type === "meta_export") {
    return (
      <div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10, marginBottom: 14 }}>
          <Metric label="Objective" value={out.campaign_objective}/>
          <Metric label="Daily budget" value={`₹${out.daily_budget_inr}`}/>
          <Metric label="Variants" value={out.variants_count}/>
        </div>
        <p style={{ marginTop: 0, color: "#5a4d6f" }}><strong>Audience + angle:</strong> {out.audience_angle}</p>
        <h5 style={{ margin: "12px 0 6px", color: "#4c1d95", fontSize: "0.85rem", letterSpacing: "0.08em", textTransform: "uppercase" }}>Launch checklist</h5>
        <ul style={{ paddingLeft: 18, lineHeight: 1.65 }}>{(out.next_steps || []).map((s, i) => <li key={i}>{s}</li>)}</ul>
      </div>
    );
  }
  if (out.type === "lesson_research") {
    return (
      <div>
        {out.topic && <p style={{ marginTop: 0, color: "#5a4d6f" }}><strong>Topic:</strong> {out.topic}</p>}
        {out.learning_goals?.length > 0 && (<>
          <h5 style={{ margin: "0 0 6px", color: "#4c1d95", fontSize: "0.85rem", letterSpacing: "0.08em", textTransform: "uppercase" }}>Learning goals</h5>
          <ul style={{ paddingLeft: 18, lineHeight: 1.6, marginBottom: 12 }}>{out.learning_goals.map((g, i) => <li key={i}>{g}</li>)}</ul>
        </>)}
        {out.prerequisites?.length > 0 && (<>
          <h5 style={{ margin: "0 0 6px", color: "#4c1d95", fontSize: "0.85rem", letterSpacing: "0.08em", textTransform: "uppercase" }}>Prerequisites</h5>
          <ul style={{ paddingLeft: 18, lineHeight: 1.6, marginBottom: 12 }}>{out.prerequisites.map((g, i) => <li key={i}>{g}</li>)}</ul>
        </>)}
        {out.common_misconceptions?.length > 0 && (<>
          <h5 style={{ margin: "0 0 6px", color: "#4c1d95", fontSize: "0.85rem", letterSpacing: "0.08em", textTransform: "uppercase" }}>Watch out for</h5>
          <ul style={{ paddingLeft: 18, lineHeight: 1.6 }}>{out.common_misconceptions.map((g, i) => <li key={i}>{g}</li>)}</ul>
        </>)}
      </div>
    );
  }
  if (out.type === "slide_deck") {
    return (
      <div>
        <p style={{ marginTop: 0, color: "#5a4d6f" }}><strong>{out.slide_count} slides</strong></p>
        {(out.slides || []).map((s, i) => (
          <div key={i} style={{ background: "#fbf8f3", borderRadius: 8, padding: "12px 14px", marginBottom: 8 }}>
            <div style={{ fontSize: "0.7rem", color: "#6d28d9", letterSpacing: "0.08em", textTransform: "uppercase", marginBottom: 4 }}>Slide {i+1}</div>
            <div style={{ fontWeight: 600, marginBottom: 6 }}>{s.title}</div>
            {s.bullets?.length > 0 && <ul style={{ paddingLeft: 18, lineHeight: 1.55, margin: "0 0 6px", fontSize: "0.9rem" }}>{s.bullets.map((b, j) => <li key={j}>{b}</li>)}</ul>}
            {s.notes && <div style={{ fontSize: "0.82rem", color: "#5a4d6f", paddingTop: 6, borderTop: "1px dashed rgba(109,40,217,0.12)" }}><strong>Notes:</strong> {s.notes}</div>}
          </div>
        ))}
      </div>
    );
  }
  if (out.type === "captions_srt") {
    return (
      <div>
        <p style={{ marginTop: 0, color: "#5a4d6f" }}><strong>{out.cue_count} cues</strong></p>
        <pre style={{ whiteSpace: "pre-wrap", background: "#fbf8f3", borderRadius: 8, padding: "12px 14px", fontFamily: "ui-monospace, monospace", fontSize: "0.82rem", lineHeight: 1.5, maxHeight: 360, overflowY: "auto", margin: 0 }}>{out.srt}</pre>
      </div>
    );
  }
  if (out.type === "pdf_export") {
    return (
      <div>
        <div className="wf-article-card">
          <Markdown>{out.markdown}</Markdown>
        </div>
        {out.note && <p style={{ marginTop: 10, fontSize: "0.85rem", color: "#5a4d6f" }}>{out.note}</p>}
      </div>
    );
  }
  if (out.type === "design_concepts") {
    return (
      <div>
        {out.kit && (
          <div style={{ background: "#fbf8f3", borderRadius: 8, padding: "10px 12px", marginBottom: 12 }}>
            <div style={{ fontWeight: 600 }}>{out.kit.name}</div>
            <div style={{ display: "flex", gap: 6, margin: "6px 0" }}>
              {(out.kit.colors || []).map((c, i) => (
                <span key={i} title={c} style={{ background: c, width: 26, height: 26, borderRadius: 6, border: "1px solid rgba(0,0,0,0.06)" }}/>
              ))}
            </div>
            {out.kit.fonts && <div style={{ fontSize: "0.85rem", color: "#5a4d6f" }}>{out.kit.fonts.heading} / {out.kit.fonts.body}</div>}
          </div>
        )}
        {(out.concepts || []).map((c, i) => (
          <div key={i} style={{ background: "#fbf8f3", borderRadius: 8, padding: "12px 14px", marginBottom: 8 }}>
            <div style={{ fontSize: "0.85rem", lineHeight: 1.55, marginBottom: 6 }}><strong>Composition:</strong> {c.composition}</div>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <span style={{ background: "#1a1422", color: "white", padding: "3px 10px", borderRadius: 4, fontSize: "0.85rem", fontWeight: 700 }}>{c.overlay}</span>
              {c.dimensions && <span style={{ fontSize: "0.78rem", color: "#5a4d6f" }}>{c.dimensions}</span>}
            </div>
            <div style={{ fontSize: "0.85rem", color: "#5a4d6f" }}><strong>Element:</strong> {c.element}</div>
          </div>
        ))}
      </div>
    );
  }
  if (out.type === "checklist") {
    return <ul style={{ paddingLeft: 18, lineHeight: 1.65 }}>{(out.items || []).map((it, i) => <li key={i}>{it}</li>)}</ul>;
  }
  if (out.type === "export_pack") {
    return (
      <div>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 12, fontSize: "0.9rem", color: "#5a4d6f" }}>
          <span><strong>Asset:</strong> {out.asset_type}</span>
          {out.kit_name && <span><strong>Brand:</strong> {out.kit_name}</span>}
        </div>
        <h5 style={{ margin: "0 0 6px", color: "#4c1d95", fontSize: "0.85rem", letterSpacing: "0.08em", textTransform: "uppercase" }}>Export sizes</h5>
        <ul style={{ paddingLeft: 18, lineHeight: 1.6, marginBottom: 12 }}>{(out.exports || []).map((e, i) => <li key={i}>{e}</li>)}</ul>
        {out.next_steps?.length > 0 && (<>
          <h5 style={{ margin: "0 0 6px", color: "#4c1d95", fontSize: "0.85rem", letterSpacing: "0.08em", textTransform: "uppercase" }}>Next steps</h5>
          <ul style={{ paddingLeft: 18, lineHeight: 1.6 }}>{out.next_steps.map((s, i) => <li key={i}>{s}</li>)}</ul>
        </>)}
      </div>
    );
  }
  if (out.type === "placeholder") {
    return <p style={{ color: "#7c2d12", display: "flex", alignItems: "center", gap: 8 }}><AlertTriangle size={14}/> {out.note}</p>;
  }
  return <pre style={{ whiteSpace: "pre-wrap", fontSize: "0.85rem", color: "#5a4d6f" }}>{JSON.stringify(out, null, 2)}</pre>;
}

function Metric({ label, value }) {
  return (
    <div style={{ background: "#ede9fe", borderRadius: 8, padding: "10px 14px", minWidth: 120 }}>
      <div style={{ fontSize: "0.7rem", color: "#6d28d9", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: "1.2rem", fontWeight: 700, color: "#1a1422" }}>{value}</div>
    </div>
  );
}

// Renders an AI-generated string as cleanly-formatted markdown.
// Handles **bold**, lists, headings, and stage notes — so scripts no longer
// look like raw "**Hook:**" text dumps.
function Markdown({ children, compact = false }) {
  if (!children || typeof children !== "string") return null;
  return (
    <div className={`wf-md ${compact ? "wf-md--compact" : ""}`}>
      <ReactMarkdown>{children}</ReactMarkdown>
    </div>
  );
}

// Small inline copy button used next to tag/hashtag/keyword rows.
function CopyChip({ value, label = "Copy" }) {
  const onCopy = (e) => {
    e.stopPropagation();
    const text = Array.isArray(value) ? value.join(" ") : (value || "");
    if (!text) return;
    navigator.clipboard.writeText(text);
    toast.success("Copied");
  };
  return (
    <button type="button" className="wf-copy-chip" onClick={onCopy} data-testid="wf-inline-copy">
      <Copy size={11}/> {label}
    </button>
  );
}

// Section label with an inline copy button on the right.
function SectionLabel({ children, copyValue }) {
  return (
    <div className="wf-section-label">
      <span>{children}</span>
      {copyValue ? <CopyChip value={copyValue} /> : null}
    </div>
  );
}

// Modal that fires when a workflow completes, recommending the next workflow.
function NextTaskModal({ trackKey, projectIdea, onClose, onPick }) {
  const key = trackKey || "";
  const suggestions = NEXT_TASK_MAP[key] || [
    { key: "social", label: "Promote on social", hint: "Captions for Insta, LinkedIn & X." },
    { key: "seo", label: "Build SEO plan", hint: "Rank around the same topic." },
  ];
  const prettyKey = key.replace(/_/g, " ") || "workflow";
  return (
    <div className="wf-modal-overlay" data-testid="wf-next-task-modal" onClick={onClose}>
      <div className="wf-modal" onClick={(e) => e.stopPropagation()}>
        <button className="wf-modal-close" onClick={onClose} data-testid="wf-next-task-close" aria-label="Close">
          <X size={16}/>
        </button>
        <div className="wf-modal-eyebrow"><Rocket size={13}/> Perform next task</div>
        <h3 className="wf-modal-title">Great — what should we build next?</h3>
        <p className="wf-modal-sub">
          {projectIdea
            ? <>Aapka <strong>{prettyKey}</strong> package ready hai. Iske aage chaliye:</>
            : <>Aapka package ready hai. Next step suggestions:</>}
        </p>
        <div className="wf-modal-options">
          {suggestions.map((s) => (
            <button
              key={s.key}
              type="button"
              className="wf-modal-option"
              onClick={() => onPick(s.key)}
              data-testid={`wf-next-task-${s.key}`}
            >
              <div>
                <div className="wf-modal-option-title">{s.label}</div>
                <div className="wf-modal-option-hint">{s.hint}</div>
              </div>
              <ArrowRight size={16}/>
            </button>
          ))}
        </div>
        <button className="wf-modal-skip" onClick={onClose} data-testid="wf-next-task-skip">
          Skip — I&rsquo;ll decide later
        </button>
      </div>
    </div>
  );
}

function textForCopy(out) {
  if (!out) return "";
  if (out.type === "options") return (out.options || []).join("\n");
  if (out.type === "outline") {
    return (out.sections || []).map((s) => `## ${s.heading}\n${(s.bullets||[]).map((b)=>`- ${b}`).join("\n")}`).join("\n\n");
  }
  if (out.type === "captions") {
    return (out.variants || []).map((v) => `${v.platform}\n${v.text}\n${(v.hashtags||[]).join(" ")}`).join("\n\n---\n\n");
  }
  return JSON.stringify(out, null, 2);
}
